import React from "react";
import Logo from "../../img/logo.svg";
import Button from "../Button";
import LocalizedLink from "../LocalizedLink";
import { CURRENT, LOCATIONS } from "../../config/cities";
import {
  CITIES_COUNT_DISPLAY,
  DOCTORS_COUNT,
  EXAMS_DONE,
  REMOVALS_DONE,
} from "../../config/trust";
import { GTM_LEAD_UMOW_SIE } from "../../config/gtm_classes";
import { CTA_CLICK, trackCta } from "../../config/ctaAnalytics";
import { useCountUp } from "../stats/useCountUp";
import { articleCtaFor, ArticleCta, CITY_PICKER_ID } from "../../config/articleCta";

import "../../styles/article_conversion.scss";

// Every booking CTA on a content page goes to the city chooser on the home
// page, not to /kontakt — picking a city is the first real step of the funnel
// and the contact page made the reader take a detour to find it.
const CITY_CHOOSER = "/#kontakt";

// Default for pages rendered without a category (service-page template, Toc):
// the screening copy every article had before intents existed.
const SCREENING = articleCtaFor(null);

// Kept for callers that still import the constant.
export const SIDEBAR_HEADLINE = SCREENING.sidebarHeadline;

const EXAMS = { value: EXAMS_DONE, label: "wideodermatoskopii" };
const REMOVALS = { value: REMOVALS_DONE, label: "usuniętych zmian" };
const STATS_TAIL = [
  { value: DOCTORS_COUNT, label: "lekarzy w zespole" },
  { value: CITIES_COUNT_DISPLAY, label: "miast w Polsce" },
];
// The figure that answers the reader's question goes first.
const statsFor = (cta: ArticleCta) =>
  cta.intent === "screening" || cta.intent === "lesion"
    ? [EXAMS, REMOVALS, ...STATS_TAIL]
    : [REMOVALS, EXAMS, ...STATS_TAIL];

// Copy lives in config/articleCta.ts. Two notes that still hold:
// - the stats headline equals the side promo's on purpose — the card is
//   mobile-only and the promo desktop-only, so one message serves both widths;
// - the overline never says "Twoje Znamiona": the logo 12px away already does.

/**
 * Blocks 2 and 4 — the same card in two roles.
 *
 * `stats` closes the first section and is the ONLY place carrying the full set
 * of numbers. `closing` ends the article and deliberately drops them: they were
 * already made, and repeating them here would read as a second pitch instead of
 * a next step. It offers the city list instead, which is what someone who read
 * to the end actually needs.
 *
 * 🔴 No heading tags inside. The side table of contents is authored in
 * frontmatter rather than parsed from the DOM, so an <h2> here would not break
 * it today — but it would put a marketing line into the document outline that
 * every future heading-based tool (TOC generation, SEO audits, screen-reader
 * navigation) reads as part of the article.
 */
const ArticleClinicCard = ({
  variant = "stats",
  cta = SCREENING,
}: {
  variant?: "stats" | "closing";
  cta?: ArticleCta;
}) => {
  const headline = variant === "closing" ? cta.closingHeadline : cta.statsHeadline;
  const stats = statsFor(cta);
  const cities = LOCATIONS.filter((location) => location.type === CURRENT);
  const { ref, values: counted } = useCountUp(stats.map((stat) => stat.value));

  return (
    <div
      className={`ac_card ac_card--${variant}`}
      data-article-card={variant}
      data-cta-surface={variant === "closing" ? "article_closing" : "article_stats_card"}
      id={variant === "closing" && cta.cities ? CITY_PICKER_ID : undefined}
    >
      <div className="ac_card_head">
        <img className="ac_card_logo" src={Logo} alt="" aria-hidden="true" />
        <p className="ac_card_overline">{cta.overline}</p>
      </div>

      <p className="ac_card_headline">{headline}</p>

      {variant === "stats" && (
        <div className="ac_card_stats" ref={ref}>
          {stats.map((stat, index) => (
            <div className="ac_card_stat" key={stat.label}>
              <span className="ac_card_stat_value">{counted[index] ?? stat.value}</span>
              <span className="ac_card_stat_label">{stat.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Removal and aftercare: the city list IS the call to action — each link
          opens that city's removal page at the price list. */}
      {cta.cities && (
        <>
          <p className="ac_card_prompt">{cta.cityPrompt}</p>
          <div className="ac_card_cities ac_card_cities--cta">
            {cta.cities.map((city) => (
              <LocalizedLink
                key={city.city}
                className={`ac_card_city ${GTM_LEAD_UMOW_SIE}`}
                to={city.to}
                data-cta-city={city.to.split("/")[1]}
              >
                {city.city}
              </LocalizedLink>
            ))}
          </div>
        </>
      )}

      {!cta.cities && variant === "closing" && (
        <div className="ac_card_cities">
          {cities.map((location) => (
            <LocalizedLink key={location.city} className="ac_card_city" to={`/${location.link}`}>
              {location.city}
            </LocalizedLink>
          ))}
        </div>
      )}

      {!cta.cities && (
        <LocalizedLink className={`ac_card_cta ${GTM_LEAD_UMOW_SIE}`} to={CITY_CHOOSER}>
          <Button size="L">
            <span className={GTM_LEAD_UMOW_SIE}>{cta.buttonLabel}</span>
          </Button>
        </LocalizedLink>
      )}

      {/* Closing card only, screening/lesion only: the reader who will not book
          today still has a photo and a worry. Analiza AI is framed per the brand
          rule — a first step while waiting, never a substitute for the visit.
          Tracked as its own cta_type so it never inflates the booking numbers. */}
      {!cta.cities && variant === "closing" && (
        <p className="ac_card_ai">
          Masz zdjęcie zmiany i czekasz na wizytę?{" "}
          <LocalizedLink
            to="/analiza/"
            onClick={() =>
              trackCta(CTA_CLICK, {
                cta_type: "ai",
                cta_surface: "article_closing",
                cta_label: "Wstępna ocena AI",
                cta_target: "/analiza/",
              })
            }
          >
            Wstępna ocena AI
          </LocalizedLink>{" "}
          wspiera samoobserwację, ale nie zastępuje badania.
        </p>
      )}
    </div>
  );
};

export default ArticleClinicCard;
